import React, { useRef, useState, useEffect } from 'react';
import type { RefObject } from 'react';
import { useDesignStore } from '../../store/designStore';
import { useFabricCanvas } from '../../hooks/useFabricCanvas';
import { useCanvasPan, useCanvasZoom } from '../../hooks/useCanvasZoom';
import { useAlignmentGuides } from '../../hooks/useAlignmentGuides';
import { crossOriginForUrl } from '../../lib/crossOriginImage';
import { Ruler, RULER_THICKNESS } from './Ruler';

interface DesignerWorkspaceProps {
  /** The scrollable ancestor (`DesignerApp.tsx`'s `workspaceRef`) — zoom/pan
   * scroll this instead of panning inside the Fabric canvas itself. See the
   * comment on `zoomCanvasAt` in `useCanvasZoom.ts` for why. */
  containerRef?: RefObject<HTMLDivElement | null>;
}

export function DesignerWorkspace({ containerRef }: DesignerWorkspaceProps) {
  const { sides, activeSideKey, canvasInstances, imageNaturalSizes, setImageNaturalSize, showGrid, showRulers } = useDesignStore();
  const activeSide = sides.find(s => s.sideKey === activeSideKey);
  const canvasRef  = useRef<HTMLCanvasElement>(null);

  const displayImagePath = activeSide?.imagePath;
  // See crossOriginImage.ts — every <img> loading a URL that designDownload.ts's
  // loadImage() might also load (Save/Preview/Download/3D) must resolve
  // crossOrigin identically for that URL, or risk a real CORS cache-race bug.
  const displayImageCrossOrigin = crossOriginForUrl(displayImagePath);

  useFabricCanvas(canvasRef, activeSide);
  const activeCanvas = canvasInstances.get(activeSideKey) ?? null;
  useCanvasPan(activeCanvas, containerRef);
  useCanvasZoom(activeCanvas, containerRef);
  // Drag-to-align guides — purple center cross-hair + lime edge/center snap
  // lines against other objects, matching the admin design-area editor's
  // existing (unmodified) behavior. See useAlignmentGuides.ts for detail.
  useAlignmentGuides(activeCanvas);

  // canvasWidth/canvasHeight is an admin-configured design-area rectangle —
  // it is NOT the product image's natural aspect ratio. Track the image's
  // own natural size (via the store's per-imagePath cache, not local state)
  // and scale it by the canvas zoom so it never stretches.
  //
  // Sourced from `imageNaturalSizes` (keyed by `imagePath`, see designStore.ts)
  // instead of a local `useState` reset on `displayImagePath` change. Two
  // different sides (e.g. the same shared product photo tagged with two
  // different print methods, see GivingEurope's TB/DO4 areas) can carry the
  // identical `imagePath` URL. Switching between them re-renders the same
  // `<img>` DOM node with an unchanged `src`, so the browser never re-fires
  // `onLoad` — a local state reset keyed on `displayImagePath` would then
  // never recover a fresh value, permanently stuck at the previous side's
  // natural size while `activeSideKey`'s own canvas geometry moved on,
  // blowing up `imgW`/`imgH` far past the real photo and forcing scrollbars.
  // Keying the cache itself by `imagePath` sidesteps the problem entirely:
  // whichever side first loaded this exact photo already populated the
  // entry every other side sharing it can read immediately, with no
  // dependency on `onLoad` firing again.
  const naturalSize = displayImagePath ? imageNaturalSizes.get(displayImagePath) ?? null : null;
  const [zoom, setZoom] = useState(() => activeCanvas?.getZoom() ?? 1);

  useEffect(() => {
    if (!activeCanvas) return;
    setZoom(activeCanvas.getZoom());
    const update = () => setZoom(activeCanvas.getZoom());
    activeCanvas.on('after:render', update);
    return () => { activeCanvas.off('after:render', update); };
  }, [activeCanvas]);

  const imgW = naturalSize ? naturalSize.w * zoom : undefined;
  const imgH = naturalSize ? naturalSize.h * zoom : undefined;

  return (
    <div
      className="designer-workspace"
      style={{
        // `width`/`height: 100%` (not just `flex: 1`, which does nothing for sizing
        // here — this div's parent, `DesignerApp.tsx`'s `workspaceRef`, isn't a flex
        // container) make this div actually fill the scrollable parent so the centering
        // below has a real box to center within, instead of shrink-wrapping to exactly
        // the content's own size (which made `alignItems`/`justifyContent: center` a
        // no-op in practice — confirmed live, content sat flush at the top-left with
        // all the slack pushed to the bottom/right). `overflow: 'visible'` (was
        // `'hidden'`) lets zoomed-in content that's bigger than this box spill outside
        // it instead of being clipped here — the actual scroll/clip boundary is the
        // parent `workspaceRef` (now `overflow: 'auto'`), which needs the true rendered
        // extent to size its scrollbars correctly.
        //
        // `alignItems`/`justifyContent: 'safe center'` (was plain `'center'`) —
        // plain `'center'` is "unsafe/default centering": once the (inline-grid)
        // content grows past this box via zoom, a centered flex item's overflow
        // gets clipped on the before/start side (left+top) with no way to scroll
        // to it, because the browser never lets that start-side overflow become
        // part of the scrollable area at all — confirmed live via Playwright: even
        // at scrollLeft=scrollTop=0 (the minimum), the zoomed canvas's top-left
        // corner sat off-screen at negative viewport coordinates, permanently
        // unreachable. That's the exact "zoom in past full screen size and it
        // blocks" bug. `safe center` is the standard CSS Box Alignment fallback for
        // this: center when content fits, but fall back to start-alignment (whose
        // overflow is fully scrollable) once it doesn't.
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'safe center',
        justifyContent: 'safe center',
        background: '#f0f0f0',
        overflow: 'visible',
        position: 'relative',
      }}
    >
      {activeSide && (
        // Always exactly 4 grid children, each explicitly placed by
        // `gridColumn`/`gridRow` — relying on CSS Grid's auto-placement here
        // was a real bug: when the two ruler cells were conditionally
        // omitted from the DOM (`showRulers && <Ruler/>`), auto-placement
        // re-flowed `content` into row 1 instead of row 2, since
        // auto-placement assigns cells to whichever children are actually
        // present, not to fixed slots. Row 1 still rendered fine (grid items'
        // implicit `min-height:auto` stopped it from actually collapsing to
        // the nominal 0px track), but the now-genuinely-empty row 2 left a
        // plain `<div>` sized to match — invisible, but still in the
        // hit-testing tree, intercepting clicks meant for the canvas beneath
        // it (caught by `visual-regression.spec.ts` failing to deselect via
        // a corner click — confirmed live via `elementFromPoint` at the exact
        // failing coordinates). Explicit placement makes the structure
        // independent of which children are conditionally rendered, and also
        // keeps `content` at the same tree position regardless of
        // `showRulers` so toggling it never disposes/remounts the live
        // Fabric canvas (confirmed live: that was a real, separate bug this
        // same explicit-placement fix also closes).
        <div style={{
          display: 'inline-grid',
          gridTemplateColumns: `${showRulers ? RULER_THICKNESS : 0}px auto`,
          gridTemplateRows: `${showRulers ? RULER_THICKNESS : 0}px auto`,
        }}>
          <div style={{ gridColumn: 1, gridRow: 1 }} />
          <div style={{ gridColumn: 2, gridRow: 1 }}>
            {showRulers && <Ruler orientation="horizontal" lengthPx={imgW ?? 0} zoom={zoom} />}
          </div>
          <div style={{ gridColumn: 1, gridRow: 2 }}>
            {showRulers && <Ruler orientation="vertical" lengthPx={imgH ?? 0} zoom={zoom} />}
          </div>
          <div
            style={{
              gridColumn: 2,
              gridRow: 2,
              position: 'relative',
              border: '1px solid #ddd',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              lineHeight: 0,
            }}
          >
            {/* Product image — scaled by zoom, keeping its own natural aspect ratio */}
            <img
              src={displayImagePath}
              alt={activeSide.label}
              crossOrigin={displayImageCrossOrigin}
              style={{ display: 'block', width: imgW, height: imgH }}
              onLoad={e => {
                const el = e.target as HTMLImageElement;
                if (displayImagePath) setImageNaturalSize(displayImagePath, el.naturalWidth, el.naturalHeight);
              }}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            {/* Fabric.js canvas overlay — offset by the admin-configured area position, scaled by the same zoom factor as the image */}
            <div style={{ position: 'absolute', top: activeSide.areaTop * zoom, left: activeSide.areaLeft * zoom }}>
              <canvas ref={canvasRef} />
            </div>
            {/* Dot-grid overlay — visual alignment aid only, toggled via BottomBar's grid
                button; never part of the canvas, never saved/printed. Dot spacing scales
                with zoom so it stays a consistent real-world spacing at any zoom level. */}
            {showGrid && (
              <div
                style={{
                  position: 'absolute',
                  top: activeSide.areaTop * zoom,
                  left: activeSide.areaLeft * zoom,
                  width: activeSide.canvasWidth * zoom,
                  height: activeSide.canvasHeight * zoom,
                  backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.28) 1px, transparent 1px)',
                  backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
            )}
            {/* Design-area guide — visual aid only; never part of the canvas, never saved/printed.
                A white outer ring + tinted fill keeps it visible against any product image color.
                Only the ACTIVE/selected placeholder is ever drawn here — a photo with several
                placeholders (e.g. GivingEurope's "at the side" photo stacking LD/LE/LR1/LR2/LC at
                different heights) used to also draw every sibling area's guide box on the same
                photo, which read as an unreadable mesh of overlapping boxes once there were more
                than 2-3. The customer reaches other placeholders via the side-tab strip or the
                header's "Print method" pill, not by clicking on the photo, so showing only the
                selected one loses no functionality. */}
            <div
              style={{
                position: 'absolute',
                top: activeSide.areaTop * zoom,
                left: activeSide.areaLeft * zoom,
                width: activeSide.canvasWidth * zoom,
                height: activeSide.canvasHeight * zoom,
                border: '2px solid var(--selection)',
                background: 'color-mix(in srgb, var(--selection) 10%, transparent)',
                boxSizing: 'border-box',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              {/* `placeholderLabel` (just this area's own short name, e.g. "at the
                  side (LC)") only when the photo has more than one placeholder —
                  `activeSide.label` is the FULL combined string (photo label +
                  area label concatenated, see useProductSetup.ts) meant for the
                  side-tab strip's tooltip, not on-canvas display; showing it here
                  produced an unreadable run-on string on any multi-placeholder
                  photo. Falls back to `photoLabel` (just the photo's own name,
                  no concatenation) for the common single-placeholder case. */}
              {/* Pinned to the box's own top-left corner (not inline margin-flowed)
                  so it sits directly over the blue border regardless of box size,
                  the same "selected item" tag convention as a marquee-selection
                  label. `lineHeight: 'normal'` overrides the ancestor `lineHeight:
                  0` (set higher up to remove the baseline gap under the product
                  <img>) — without it this span's own box collapsed to ~4px tall
                  and the text rendered overflowing outside it, which read as
                  stray white-line artifacts around the label. */}
              {(activeSide.placeholderLabel ?? activeSide.photoLabel) && (
                <span style={{
                  position: 'absolute', top: -1, left: -2, transform: 'translateY(-100%)',
                  display: 'inline-block', fontSize: 10, fontWeight: 600, lineHeight: 'normal',
                  whiteSpace: 'nowrap', color: '#1e293b', background: '#fff', padding: '2px 6px', borderRadius: '3px 3px 3px 0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}>
                  {activeSide.placeholderLabel ?? activeSide.photoLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
